/**
 * @module  public/js/ui/modals/linkedin-modal.js
 * @summary Small, framework‑free modal for logging LinkedIn activity.
 */
import { applyFocusModeTooltips } from '../../focusmode/tooltipService.js';

export class LinkedInModal {
  /**
   * @param {object} params
   * @param {string} params.rootId – DOM id of the modal root element
   */
  constructor({ rootId }) {
    this.root = document.getElementById(rootId);
    if (!this.root) throw new Error('LinkedInModal: root element not found');
  }

  /**
   * Open the modal.
   * @param {object} params
   * @param {(actions: string[]) => void} params.onSave
   * @param {() => void} [params.onCancel]
   */
  open({ onSave, onCancel }) {
    this.root.classList.remove('hidden');
    this.#setLoading(false);
    applyFocusModeTooltips(this.root, {
      enableTooltips:
        document.body?.getAttribute('data-focus-tooltips') !== 'false',
    });
    const saveBtn = this.root.querySelector('[data-action="save"]');
    const cancelBtn = this.root.querySelector('[data-action="cancel"]');
    saveBtn.onclick = async () => {
      const selected = Array.from(
        this.root.querySelectorAll('input[name="liAction"]:checked')
      ).map((el) => el.value);
      if (!selected.length) return;
      try {
        this.#setLoading(true);
        await onSave(selected);
        this.close();
      } catch (err) {
        console.error('Failed to save LinkedIn outcome', err);
        this.#setLoading(false);
      }
    };
    cancelBtn.onclick = () => {
      onCancel?.();
      this.close();
    };
  }

  /** Close and reset the modal. */
  close() {
    this.#setLoading(false);
    this.root.classList.add('hidden');
    this.root
      .querySelectorAll('input[name="liAction"]')
      .forEach((el) => (el.checked = false));
    const saveBtn = this.root.querySelector('[data-action="save"]');
    const cancelBtn = this.root.querySelector('[data-action="cancel"]');
    saveBtn.onclick = null;
    cancelBtn.onclick = null;
  }

  /**
   * Toggle spinner visibility and temporarily lock the form while saving.
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    const spinner = this.root.querySelector('[data-role="saving-indicator"]');
    const form = this.root.querySelector('#linkedinForm');
    const actionButtons = this.root.querySelectorAll('button[data-action]');
    const options = this.root.querySelectorAll('input[name="liAction"]');

    if (spinner) spinner.classList.toggle('hidden', !isLoading);
    if (form) form.classList.toggle('hidden', isLoading);

    options.forEach((input) => {
      input.disabled = isLoading;
    });

    actionButtons.forEach((btn) => {
      btn.disabled = isLoading;
      if (isLoading) {
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.removeAttribute('aria-disabled');
      }
    });

    if (isLoading) {
      this.root.setAttribute('aria-busy', 'true');
    } else {
      this.root.removeAttribute('aria-busy');
    }
  }
}
